import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../../context/DeviceContext';
import { ERROR_CODE } from '../../services/apiError';
import authService from '../../services/auth';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import './LinkDevice.css';

/** Mensajes por código de error. La interfaz nunca decide según el texto. */
const MENSAJES = {
  [ERROR_CODE.INVALID_CODE]: {
    titulo: 'Los datos no coinciden',
    texto: 'Revisa el número de serie y el código de activación. Ambos están en la etiqueta de la parte inferior del dispositivo.',
  },
  [ERROR_CODE.ALREADY_LINKED_BY_YOU]: {
    titulo: 'Ya lo tienes vinculado',
    texto: 'Este dispositivo ya está en tu cuenta. Puedes verlo en la lista de dispositivos.',
    accion: { texto: 'Ver mis dispositivos', ruta: '/devices' },
  },
  [ERROR_CODE.ALREADY_LINKED_BY_OTHER]: {
    titulo: 'Pertenece a otra cuenta',
    texto: 'Si compraste este equipo de segunda mano, la cuenta anterior debe desvincularlo primero. Escríbenos si necesitas ayuda.',
  },
  [ERROR_CODE.LIMIT_REACHED]: {
    titulo: 'Alcanzaste el límite de tu plan',
    texto: 'Mejora tu plan para vincular más dispositivos.',
    accion: { texto: 'Ver planes', ruta: '/subscription' },
  },
  [ERROR_CODE.NETWORK]: {
    titulo: 'Sin conexión',
    texto: 'No pudimos contactar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
  },
};

/** Da formato al serial mientras se escribe: NG-001-4F2A */
function formatearSerial(v) {
  const limpio = v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 9);
  const p = [limpio.slice(0, 2), limpio.slice(2, 5), limpio.slice(5, 9)].filter(Boolean);
  return p.join('-');
}

/** Da formato al código: A7K2-9XQ4 */
function formatearCodigo(v) {
  const limpio = v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return limpio.length > 4 ? `${limpio.slice(0, 4)}-${limpio.slice(4)}` : limpio;
}

export default function LinkDevice() {
  const navigate = useNavigate();
  const { linkDevice, linking } = useDevices();

  const [serial, setSerial] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);
  const [ayuda, setAyuda] = useState(false);
  const [codigos, setCodigos] = useState([]);
  const serialRef = useRef(null);
  const esDemo = authService.isDemo();

  useEffect(() => { serialRef.current?.focus(); }, []);

  // Los códigos de prueba sólo se cargan en demostración, y con importación
  // diferida: así el módulo de simulación no entra en el paquete de un usuario
  // real que nunca lo va a usar.
  useEffect(() => {
    if (!esDemo) return;
    import('../../services/mock/mockBackend')
      .then((m) => setCodigos(m.codigosDePrueba()))
      .catch(() => setCodigos([]));
  }, [esDemo]);

  const listo = serial.replace(/-/g, '').length === 9 && codigo.replace(/-/g, '').length === 8;

  const enviar = async (e) => {
    e.preventDefault();
    if (!listo || linking) return;
    setError(null);
    try {
      const nuevo = await linkDevice({ serial, code: codigo });
      setExito(nuevo);
    } catch (err) {
      setError(err);
    }
  };

  /* ---------- Éxito ---------- */
  if (exito) {
    return (
      <div className="ld-page">
        <div className="ld-card ld-card--result">
          <span className="ld-result__icon is-success"><CheckCircleRoundedIcon /></span>
          <h2>Dispositivo vinculado</h2>
          <p className="ld-result__text">
            <strong>{exito.device_name}</strong> ya forma parte de tu cuenta. Aparecerá como
            desconectado hasta que lo enciendas y se conecte a tu red WiFi por primera vez.
          </p>
          <div className="ld-result__actions">
            <button className="ld-btn ld-btn--primary" onClick={() => navigate('/home')}>
              Ir al panel
            </button>
            <button
              className="ld-btn"
              onClick={() => { setExito(null); setSerial(''); setCodigo(''); serialRef.current?.focus(); }}
            >
              Vincular otro
            </button>
          </div>
        </div>
      </div>
    );
  }

  const msg = error ? MENSAJES[error.code] ?? MENSAJES[ERROR_CODE.NETWORK] : null;

  return (
    <div className="ld-page">
      <button className="ld-back" onClick={() => navigate(-1)}>
        <ArrowBackRoundedIcon fontSize="small" /> Volver
      </button>

      <div className="ld-card">
        <header className="ld-head">
          <span className="ld-head__icon"><QrCodeScannerRoundedIcon /></span>
          <div>
            <h2>Vincular un dispositivo</h2>
            <p>Introduce los datos que aparecen en la etiqueta de tu NisaMeter.</p>
          </div>
        </header>

        <form className="ld-form" onSubmit={enviar}>
          <label className="ld-field">
            <span className="ld-field__label">Número de serie</span>
            <input
              ref={serialRef}
              className="ld-input"
              value={serial}
              onChange={(e) => { setSerial(formatearSerial(e.target.value)); setError(null); }}
              placeholder="NG-000-0000"
              inputMode="text"
              autoComplete="off"
              spellCheck="false"
              disabled={linking}
            />
          </label>

          <label className="ld-field">
            <span className="ld-field__label">Código de activación</span>
            <input
              className="ld-input"
              value={codigo}
              onChange={(e) => { setCodigo(formatearCodigo(e.target.value)); setError(null); }}
              placeholder="XXXX-XXXX"
              inputMode="text"
              autoComplete="off"
              spellCheck="false"
              disabled={linking}
            />
          </label>

          {msg && (
            <div className="ld-alert ld-alert--error" role="alert">
              <ErrorRoundedIcon fontSize="small" />
              <div>
                <strong>{msg.titulo}</strong>
                <span>{msg.texto}</span>
              </div>
              {msg.accion && (
                <button type="button" className="ld-alert__action" onClick={() => navigate(msg.accion.ruta)}>
                  {msg.accion.texto}
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            className="ld-btn ld-btn--primary ld-btn--block"
            disabled={!listo || linking}
          >
            {linking ? (<><span className="ld-spinner" aria-hidden="true" />Verificando…</>)
                     : 'Vincular dispositivo'}
          </button>
        </form>

        <div className="ld-scan">
          <span className="ld-scan__rule" />
          <span>o</span>
          <span className="ld-scan__rule" />
        </div>
        {/* Deshabilitado a propósito: la cámara llega cuando exista el backend,
            porque el QR solo tiene sentido si el código que contiene se valida. */}
        <button className="ld-btn ld-btn--ghost ld-btn--block" disabled title="Disponible próximamente">
          <QrCodeScannerRoundedIcon fontSize="small" />
          Escanear código QR
          <span className="ld-soon">Próximamente</span>
        </button>
      </div>

      {/* SÓLO en la cuenta de demostración. Un usuario real vincula su equipo
          con la etiqueta que trae en la caja, y ver aquí una lista de códigos
          ajenos sería desconcertante además de incorrecto. La redacción es
          neutra a propósito: la cuenta de demostración no se anuncia como tal. */}
      {esDemo && (
        <div className="ld-help">
          <button className="ld-help__toggle" onClick={() => setAyuda((v) => !v)}>
            <InfoRoundedIcon fontSize="small" />
            {ayuda ? 'Ocultar códigos disponibles' : 'Ver códigos disponibles'}
          </button>
          {ayuda && (
            <div className="ld-help__body">
              <p>Usa cualquiera de estos pares para vincular un equipo:</p>
              <table className="ld-table">
                <thead>
                  <tr><th>Serie</th><th>Código</th><th>Resultado</th></tr>
                </thead>
                <tbody>
                  {codigos.map((c) => (
                    <tr key={c.serial}>
                      <td><code>{c.serial}</code></td>
                      <td><code>{c.code}</code></td>
                      <td>{c.estado}</td>
                    </tr>
                  ))}
                  <tr>
                    <td><code>NG-999-0000</code></td>
                    <td><code>0000-0000</code></td>
                    <td>datos incorrectos</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
